'use client';

import { useCallback } from 'react';
import {
  AlertCircle,
  Check,
  FileIcon,
  Loader2,
  Pause,
  Play,
  RotateCcw,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Progress } from '@kit/ui/progress';
import { cn } from '@kit/ui/utils';

import type { UploadStatus as HookUploadStatus } from '../hooks/use-chunked-upload';
import type { Document } from '../types';

// Re-export the unified UploadStatus type (extends hook type with 'pending')
export type UploadStatus = HookUploadStatus | 'pending';

export interface UploadQueueItem {
  id: string;
  file: File;
  status: UploadStatus;
  progress: number;
  error?: string;
  result?: Document;
  previewUrl?: string;
}

interface DocumentUploadQueueProps {
  items: UploadQueueItem[];
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onCancel?: (id: string) => void;
  showThumbnails?: boolean;
  className?: string;
}

/**
 * Upload queue component showing progress for multiple file uploads
 *
 * @example
 * ```tsx
 * <DocumentUploadQueue
 *   items={uploadQueue}
 *   onRemove={(id) => removeFromQueue(id)}
 *   onRetry={(id) => retryUpload(id)}
 *   onPause={(id) => pauseUpload(id)}
 *   onResume={(id) => resumeUpload(id)}
 *   showThumbnails
 * />
 * ```
 */
export function DocumentUploadQueue({
  items,
  onRemove,
  onRetry,
  onPause,
  onResume,
  onCancel,
  showThumbnails = true,
  className,
}: DocumentUploadQueueProps) {
  const { t } = useTranslation();

  if (items.length === 0) {
    return null;
  }

  const completedCount = items.filter((i) => i.status === 'completed').length;
  const failedCount = items.filter((i) => i.status === 'failed').length;
  const pendingCount = items.filter(
    (i) => i.status === 'pending' || i.status === 'uploading',
  ).length;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Summary */}
      <div className="text-muted-foreground flex items-center justify-between text-sm">
        <span>
          {pendingCount > 0 &&
            `${t('documents:queue.uploadingCount', { count: pendingCount })} • `}
          {completedCount > 0 &&
            t('documents:queue.completedCount', { count: completedCount })}
          {failedCount > 0 &&
            ` • ${t('documents:queue.failedCount', { count: failedCount })}`}
        </span>
        <span>{t('documents:queue.totalCount', { count: items.length })}</span>
      </div>

      {/* Queue items */}
      <div className="space-y-2">
        {items.map((item) => (
          <QueueItem
            key={item.id}
            item={item}
            onRemove={onRemove}
            onRetry={onRetry}
            onPause={onPause}
            onResume={onResume}
            onCancel={onCancel}
            showThumbnail={showThumbnails}
          />
        ))}
      </div>
    </div>
  );
}

interface QueueItemProps {
  item: UploadQueueItem;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onCancel?: (id: string) => void;
  showThumbnail?: boolean;
}

function QueueItem({
  item,
  onRemove,
  onRetry,
  onPause,
  onResume,
  onCancel,
  showThumbnail,
}: QueueItemProps) {
  const { t } = useTranslation();
  const handleRemove = useCallback(
    () => onRemove(item.id),
    [item.id, onRemove],
  );
  const handleRetry = useCallback(() => onRetry(item.id), [item.id, onRetry]);
  const handlePause = useCallback(() => onPause?.(item.id), [item.id, onPause]);
  const handleResume = useCallback(
    () => onResume?.(item.id),
    [item.id, onResume],
  );
  const handleCancel = useCallback(
    () => onCancel?.(item.id),
    [item.id, onCancel],
  );

  const canPause = item.status === 'uploading' && onPause;
  const canResume = item.status === 'paused' && onResume;
  const canCancel =
    (item.status === 'uploading' || item.status === 'paused') && onCancel;
  const canRetry = item.status === 'failed';
  const canRemove = item.status === 'completed' || item.status === 'failed';

  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        item.status === 'failed' && 'border-destructive/50 bg-destructive/5',
        item.status === 'completed' && 'bg-muted/30',
      )}
    >
      <div className="flex items-start gap-3">
        {/* Thumbnail / Icon */}
        <div className="flex-shrink-0">
          {showThumbnail && item.previewUrl ? (
            // eslint-disable-next-line @next/next/no-img-element, ballee-media/no-untransformed-media -- blob: preview of an in-flight upload, never R2; cdnImageUrl would pass it through unchanged.
            <img
              src={item.previewUrl}
              alt={item.file.name}
              className="h-12 w-12 rounded object-cover"
            />
          ) : (
            <div
              className={cn(
                'bg-muted flex h-12 w-12 items-center justify-center rounded',
                item.status === 'completed' && 'bg-success/10',
                item.status === 'failed' && 'bg-destructive/10',
              )}
            >
              {item.status === 'completed' ? (
                <Check className="text-success h-5 w-5" />
              ) : item.status === 'failed' ? (
                <AlertCircle className="text-destructive h-5 w-5" />
              ) : (
                <FileIcon className="text-muted-foreground h-5 w-5" />
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{item.file.name}</p>
              <p className="text-muted-foreground text-xs">
                {formatFileSize(item.file.size)}
                {item.status === 'uploading' && ` • ${item.progress}%`}
                {item.status === 'paused' &&
                  ` • ${t('documents:queue.paused')}`}
                {item.status === 'completed' &&
                  ` • ${t('documents:queue.completed')}`}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-shrink-0 items-center gap-1">
              {canPause && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handlePause}
                  aria-label={t('documents:queue.pauseUpload')}
                >
                  <Pause className="h-3.5 w-3.5" />
                </Button>
              )}
              {canResume && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleResume}
                  aria-label={t('documents:queue.resumeUpload')}
                >
                  <Play className="h-3.5 w-3.5" />
                </Button>
              )}
              {canCancel && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleCancel}
                  aria-label={t('documents:queue.cancelUpload')}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
              {canRetry && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleRetry}
                  aria-label={t('documents:queue.retryUpload')}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
              )}
              {canRemove && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleRemove}
                  aria-label={t('documents:queue.removeFromQueue')}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
              {item.status === 'pending' && (
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              )}
            </div>
          </div>

          {/* Progress bar */}
          {(item.status === 'uploading' || item.status === 'paused') && (
            <Progress value={item.progress} className="mt-2 h-1.5" />
          )}

          {/* Error message */}
          {item.status === 'failed' && item.error && (
            <p className="text-destructive mt-1 text-xs">{item.error}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
